/**
 * Every link from the app into the docs (ams-docs) resolves here, so this file is the list of what
 * breaks when a doc moves. ams-docs sets no custom slugs, so a page's URL is its file path under
 * docs/eclipse without the extension (index.md is its folder), and a section's anchor is its
 * heading lowercased and hyphenated, punctuation dropped.
 */
export const HELP_URL = 'https://docs.amscontrols.com/eclipse';

/** A docs page, optionally scrolled to one of its sections. */
export function helpPageUrl(page: string, section?: string): string {
  return `${HELP_URL}/${page}` + (section ? `#${section}` : '');
}

/** Sections linked from in-page help, for when a page's general help is too broad. */
export const HELP_SECTIONS = {
  missingMaterialDetails: helpPageUrl('integration/table-import', 'missing-material-details-pro-only'),
  speedImport: helpPageUrl('guides/performance-standards', 'editing-run-speeds-in-a-spreadsheet'),
} as const;

/**
 * App path → the most specific docs page for it, for the header's Help. A path covers everything
 * below it (/orders covers /orders/123); paths with no entry fall back to the docs home.
 */
export const HELP_LINKS: Readonly<Record<string, string>> = {
  '/orders': 'core-concepts/orders',
  '/schedule/trucks': 'core-concepts/truck-schedule',
  '/punching': 'guides/shape-punching',
  '/punching/patterns': 'guides/shape-punching/punch-patterns',
  '/punching/shapes': 'guides/shape-punching/shape-tables-and-tooling',
  '/punching/shape-aliases': 'guides/shape-punching/shape-tables-and-tooling',
  '/punching/solutions': 'guides/shape-punching/troubleshooting',
  '/machines/devices': 'machine-buddy',
  '/report/quality-audit': 'guides/quality-audits',
  '/settings/performancestandards': 'guides/performance-standards',
  '/settings/quality-audits': 'guides/quality-audits',
  '/settings/query-lists': 'guides/query-lists',
  '/settings/integration': 'integration',
  '/settings/printing': 'printing/html-reference',
  '/settings/printing-preview': 'printing/html-reference',
  '/settings/update': 'deployment/update-eclipse',
};

/** The help page for an app URL: its longest whole-segment match in HELP_LINKS, else the docs home. */
export function helpUrlFor(url: string): string {
  const segments = (url ?? '')
    .split(/[?#]/)[0]
    .split('/')
    .map((segment) => segment.split(';')[0]) // Drop matrix params.
    .filter(Boolean);
  for (let length = segments.length; length > 0; length--) {
    const page = HELP_LINKS['/' + segments.slice(0, length).join('/')];
    if (page) {
      return helpPageUrl(page);
    }
  }
  return HELP_URL;
}
